/**
 * ColumnSelector — compact toolbar replacing the chip-per-column bar.
 *
 * Provides:
 *  - Searchable multi-select dropdown for column toggling
 *  - Random N preset
 *  - Interesting N preset (linear / quadratic / cubic / exponential R² ranking)
 *  - Adjustable N control
 *  - Color-by dropdown (pass-through)
 */

import {
  memo,
  useCallback,
  useMemo,
  useRef,
  useState,
  useEffect,
  type CSSProperties,
} from "react";
import {
  rankColumns,
  rankColumnsByCorrelationTo,
  randomColumns,
  rankZCandidates,
  type ColumnDef,
  type RelationshipType,
  type ZCandidateScore,
} from "./correlationRanking";

// ---------------------------------------------------------------------------
// Shared styles (matching DataExplorer's btnStyle / selectStyle)
// ---------------------------------------------------------------------------

const BTN: CSSProperties = {
  padding: "2px 8px",
  borderRadius: 4,
  border: "1px solid var(--gray-a6)",
  fontSize: 12,
  cursor: "pointer",
  background: "var(--gray-a3)",
  color: "var(--gray-12)",
  whiteSpace: "nowrap",
};

const SELECT: CSSProperties = {
  padding: "3px 4px",
  border: "1px solid var(--gray-a6)",
  borderRadius: 4,
  background: "var(--color-surface)",
  color: "var(--gray-12)",
  fontSize: 12,
};

const RELATIONSHIP_LABELS: Record<RelationshipType, string> = {
  linear: "Linear",
  quadratic: "Quadratic",
  cubic: "Cubic",
  exponential: "Exponential",
};

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export const AUTO_GROUP_KEY = "__auto";

export interface ColumnSelectorProps {
  /** All numeric columns (already filtered for constants by parent). */
  columns: ColumnDef[];
  /** Currently selected column keys. */
  selectedKeys: Set<string>;
  /** Update the full selection set. */
  onSelectionChange: (keys: Set<string>) => void;
  /** Rows for computing rankings. */
  rows: Record<string, unknown>[];
  /** Color-by state. */
  colorByKey?: string;
  onColorByChange: (key: string | undefined) => void;
  /** All columns eligible for color-by (including categoricals). */
  colorByOptions: ColumnDef[];
  /** Group-by state (undefined = none, AUTO_GROUP_KEY = auto, field = manual). */
  groupByKey?: string;
  onGroupByChange?: (key: string | undefined) => void;
  /** Size-by (numeric field). */
  sizeByKey?: string;
  onSizeByChange?: (key: string | undefined) => void;
  /** Symbol-by (categorical field). */
  symbolByKey?: string;
  onSymbolByChange?: (key: string | undefined) => void;
  /** Z-axis field for 3D correlogram (numeric field). */
  zAxisKey?: string;
  onZAxisChange?: (key: string | undefined) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const ColumnSelector = memo(function ColumnSelector({
  columns,
  selectedKeys,
  onSelectionChange,
  rows,
  colorByKey,
  onColorByChange,
  colorByOptions,
  groupByKey,
  onGroupByChange,
  sizeByKey,
  onSizeByChange,
  symbolByKey,
  onSymbolByChange,
  zAxisKey,
  onZAxisChange,
}: ColumnSelectorProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [n, setN] = useState(10);
  const [relType, setRelType] = useState<RelationshipType>("linear");
  const [relOpen, setRelOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const relRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Close popover on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Close relationship dropdown on outside click
  useEffect(() => {
    if (!relOpen) return;
    const handler = (e: MouseEvent) => {
      if (relRef.current && !relRef.current.contains(e.target as Node)) {
        setRelOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [relOpen]);

  const filtered = useMemo(() => {
    if (!search) return columns;
    const q = search.toLowerCase();
    return columns.filter(
      c =>
        c.field.toLowerCase().includes(q) ||
        (c.headerName ?? "").toLowerCase().includes(q),
    );
  }, [columns, search]);

  const toggle = useCallback(
    (field: string) => {
      const next = new Set(selectedKeys);
      if (next.has(field)) next.delete(field);
      else next.add(field);
      onSelectionChange(next);
    },
    [selectedKeys, onSelectionChange],
  );

  const selectAll = useCallback(() => {
    onSelectionChange(new Set(columns.map(c => c.field)));
  }, [columns, onSelectionChange]);

  const selectNone = useCallback(() => {
    onSelectionChange(new Set());
  }, [onSelectionChange]);

  const handleRandom = useCallback(() => {
    const picked = randomColumns(columns, n);
    onSelectionChange(new Set(picked));
  }, [columns, n, onSelectionChange]);

  const handleInteresting = useCallback(
    (type: RelationshipType) => {
      const ranked = rankColumns(rows, columns, type);
      onSelectionChange(new Set(ranked.slice(0, n)));
      setRelType(type);
      setRelOpen(false);
    },
    [rows, columns, n, onSelectionChange],
  );

  const handleCorrelated = useCallback(() => {
    if (!zAxisKey) return;
    const ranked = rankColumnsByCorrelationTo(rows, columns, zAxisKey);
    const picked = [
      zAxisKey,
      ...ranked.slice(0, Math.max(0, n - 1)).map((item) => item.field),
    ].filter((field) => columns.some((column) => column.field === field));
    onSelectionChange(new Set(picked));
  }, [columns, n, onSelectionChange, rows, zAxisKey]);

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        padding: "4px 12px",
        borderBottom: "1px solid var(--gray-a5)",
        flexWrap: "wrap",
      }}
    >
      {/* ---- Column dropdown trigger ---- */}
      <div style={{ position: "relative" }}>
        <button
          ref={triggerRef}
          onClick={() => setOpen(o => !o)}
          style={{
            ...BTN,
            background: open ? "var(--accent-a4)" : "var(--gray-a3)",
            minWidth: 110,
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          Columns
          <span
            style={{
              background: "var(--accent-9)",
              color: "white",
              borderRadius: 8,
              padding: "0 5px",
              fontSize: 10,
              lineHeight: "16px",
            }}
          >
            {selectedKeys.size}/{columns.length}
          </span>
          <span style={{ fontSize: 9, marginLeft: 2 }}>&#9662;</span>
        </button>

        {open && (
          <div
            ref={popoverRef}
            style={{
              position: "absolute",
              top: "calc(100% + 4px)",
              left: 0,
              zIndex: 50,
              width: 280,
              maxHeight: 360,
              background: "var(--color-surface, #1a1a2e)",
              border: "1px solid var(--gray-a5)",
              borderRadius: 8,
              boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Search input */}
            <div style={{ padding: "8px 8px 4px" }}>
              <input
                autoFocus
                placeholder="Search columns..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  padding: "5px 8px",
                  border: "1px solid var(--gray-a5)",
                  borderRadius: 4,
                  background: "var(--gray-a2)",
                  color: "var(--gray-12)",
                  fontSize: 12,
                  outline: "none",
                }}
              />
            </div>

            {/* Quick actions */}
            <div
              style={{
                display: "flex",
                gap: 4,
                padding: "4px 8px",
                borderBottom: "1px solid var(--gray-a4)",
              }}
            >
              <button onClick={selectAll} style={{ ...BTN, fontSize: 11 }}>
                All
              </button>
              <button onClick={selectNone} style={{ ...BTN, fontSize: 11 }}>
                None
              </button>
              <button
                onClick={() => {
                  const vis = new Set(filtered.map(c => c.field));
                  onSelectionChange(
                    new Set([...selectedKeys].filter(k => vis.has(k)).length > 0
                      ? [...selectedKeys].filter(k => !vis.has(k))
                      : [...selectedKeys, ...filtered.map(c => c.field)]),
                  );
                }}
                style={{ ...BTN, fontSize: 11, marginLeft: "auto" }}
              >
                Toggle visible
              </button>
            </div>

            {/* Column list */}
            <div
              style={{
                flex: 1,
                overflowY: "auto",
                padding: "4px 0",
              }}
            >
              {filtered.length === 0 && (
                <div
                  style={{
                    padding: "12px 8px",
                    textAlign: "center",
                    color: "var(--gray-9)",
                    fontSize: 12,
                  }}
                >
                  No matching columns
                </div>
              )}
              {filtered.map(col => (
                <label
                  key={col.field}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "3px 10px",
                    cursor: "pointer",
                    fontSize: 12,
                    color: "var(--gray-12)",
                  }}
                  onMouseEnter={e =>
                    ((e.currentTarget as HTMLElement).style.background =
                      "var(--gray-a3)")
                  }
                  onMouseLeave={e =>
                    ((e.currentTarget as HTMLElement).style.background =
                      "transparent")
                  }
                >
                  <input
                    type="checkbox"
                    checked={selectedKeys.has(col.field)}
                    onChange={() => toggle(col.field)}
                    style={{ accentColor: "var(--accent-9)" }}
                  />
                  <span
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {col.headerName ?? col.field}
                  </span>
                </label>
              ))}
            </div>
          </div>
        )}
      </div>

      <div
        style={{
          width: 1,
          height: 16,
          background: "var(--gray-a5)",
        }}
      />

      {/* ---- N input ---- */}
      <label
        style={{
          display: "flex",
          alignItems: "center",
          gap: 4,
          fontSize: 11,
          color: "var(--gray-11)",
        }}
      >
        N:
        <input
          type="number"
          min={2}
          max={20}
          value={n}
          onChange={e => {
            const v = parseInt(e.target.value, 10);
            if (v >= 2 && v <= 20) setN(v);
          }}
          style={{
            ...SELECT,
            width: 44,
            textAlign: "center",
            MozAppearance: "textfield",
          }}
        />
      </label>

      {/* ---- Random ---- */}
      <button onClick={handleRandom} style={BTN}>
        Random {n}
      </button>

      {/* ---- Interesting (split button) ---- */}
      <div style={{ position: "relative", display: "flex" }}>
        <button
          onClick={() => handleInteresting(relType)}
          style={{
            ...BTN,
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0,
            borderRight: "none",
          }}
        >
          Interesting {n}
          <span style={{ fontSize: 10, color: "var(--gray-9)", marginLeft: 4 }}>
            ({RELATIONSHIP_LABELS[relType]})
          </span>
        </button>
        <button
          onClick={() => setRelOpen(o => !o)}
          style={{
            ...BTN,
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0,
            padding: "2px 4px",
          }}
        >
          &#9662;
        </button>

        {relOpen && (
          <div
            ref={relRef}
            style={{
              position: "absolute",
              top: "calc(100% + 4px)",
              left: 0,
              zIndex: 50,
              background: "var(--color-surface, #1a1a2e)",
              border: "1px solid var(--gray-a5)",
              borderRadius: 6,
              boxShadow: "0 4px 16px rgba(0,0,0,0.3)",
              minWidth: 140,
            }}
          >
            {(Object.keys(RELATIONSHIP_LABELS) as RelationshipType[]).map(
              type => (
                <button
                  key={type}
                  onClick={() => handleInteresting(type)}
                  style={{
                    display: "block",
                    width: "100%",
                    textAlign: "left",
                    padding: "6px 12px",
                    border: "none",
                    background:
                      type === relType ? "var(--accent-a4)" : "transparent",
                    color: "var(--gray-12)",
                    fontSize: 12,
                    cursor: "pointer",
                  }}
                  onMouseEnter={e =>
                    ((e.currentTarget as HTMLElement).style.background =
                      type === relType
                        ? "var(--accent-a4)"
                        : "var(--gray-a3)")
                  }
                  onMouseLeave={e =>
                    ((e.currentTarget as HTMLElement).style.background =
                      type === relType ? "var(--accent-a4)" : "transparent")
                  }
                >
                  {RELATIONSHIP_LABELS[type]}
                  {type === relType && (
                    <span style={{ marginLeft: 8, fontSize: 10 }}>&#10003;</span>
                  )}
                </button>
              ),
            )}
          </div>
        )}
      </div>

      {/* ---- Target correlation ---- */}
      <button
        onClick={handleCorrelated}
        disabled={!zAxisKey}
        title={zAxisKey ? `Select the ${n} columns most correlated with the active Z response.` : "Pick a Z response first."}
        style={{
          ...BTN,
          opacity: zAxisKey ? 1 : 0.5,
          cursor: zAxisKey ? "pointer" : "not-allowed",
        }}
      >
        Correlated {n}
      </button>

      <div
        style={{
          width: 1,
          height: 16,
          background: "var(--gray-a5)",
          margin: "0 4px",
        }}
      />

      {/* ---- Group by ---- */}
      {onGroupByChange && (
        <label style={{ fontSize: 11, color: "var(--gray-11)", whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 4 }}>
          Group:
          <select
            value={groupByKey ?? ""}
            onChange={e => onGroupByChange(e.target.value || undefined)}
            style={{ ...SELECT, fontSize: 11, maxWidth: 140 }}
          >
            <option value="">None</option>
            <option value={AUTO_GROUP_KEY}>Auto (Smart)</option>
            {colorByOptions.map(col => (
              <option key={col.field} value={col.field}>{col.headerName ?? col.field}</option>
            ))}
          </select>
        </label>
      )}

      {/* ---- Color by ---- */}
      <label
        style={{
          fontSize: 11,
          color: groupByKey ? "var(--gray-8)" : "var(--gray-11)",
          whiteSpace: "nowrap",
          display: "flex",
          alignItems: "center",
          gap: 4,
        }}
      >
        Color:
        <select
          value={colorByKey ?? ""}
          onChange={e => onColorByChange(e.target.value || undefined)}
          disabled={!!groupByKey}
          style={{ ...SELECT, fontSize: 11, maxWidth: 140, opacity: groupByKey ? 0.5 : 1 }}
        >
          <option value="">None</option>
          {colorByOptions.map(col => (
            <option key={col.field} value={col.field}>
              {col.headerName ?? col.field}
            </option>
          ))}
        </select>
      </label>

      {/* ---- Size by ---- */}
      {onSizeByChange && (
        <label style={{ fontSize: 11, color: "var(--gray-11)", whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 4 }}>
          Size:
          <select
            value={sizeByKey ?? ""}
            onChange={e => onSizeByChange(e.target.value || undefined)}
            style={{ ...SELECT, fontSize: 11, maxWidth: 120 }}
          >
            <option value="">None</option>
            {columns.map(col => (
              <option key={col.field} value={col.field}>{col.headerName ?? col.field}</option>
            ))}
          </select>
        </label>
      )}

      {/* ---- Symbol by ---- */}
      {onSymbolByChange && (
        <label style={{ fontSize: 11, color: "var(--gray-11)", whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 4 }}>
          Symbol:
          <select
            value={symbolByKey ?? ""}
            onChange={e => onSymbolByChange(e.target.value || undefined)}
            style={{ ...SELECT, fontSize: 11, maxWidth: 120 }}
          >
            <option value="">None</option>
            {colorByOptions.map(col => (
              <option key={col.field} value={col.field}>{col.headerName ?? col.field}</option>
            ))}
          </select>
        </label>
      )}

      {/* ---- Z-axis (3D) ---- */}
      {onZAxisChange && (
        <ZAxisSelector
          columns={columns}
          selectedKeys={selectedKeys}
          rows={rows}
          zAxisKey={zAxisKey}
          onZAxisChange={onZAxisChange}
        />
      )}
    </div>
  );
});

// ---------------------------------------------------------------------------
// Z-axis selector with "Interesting Z" auto-detection
// ---------------------------------------------------------------------------

const ZAxisSelector = memo(function ZAxisSelector({
  columns,
  selectedKeys,
  rows,
  zAxisKey,
  onZAxisChange,
}: {
  columns: ColumnDef[];
  selectedKeys: Set<string>;
  rows: Record<string, unknown>[];
  zAxisKey?: string;
  onZAxisChange: (key: string | undefined) => void;
}) {
  // Rank Z candidates based on selected columns
  const rankedZ = useMemo<ZCandidateScore[]>(() => {
    if (selectedKeys.size < 2 || rows.length < 4) return [];
    const selectedCols = columns.filter(c => selectedKeys.has(c.field));
    return rankZCandidates(rows, selectedCols, columns);
  }, [rows, columns, selectedKeys]);

  const topZ = rankedZ.length > 0 ? rankedZ[0]! : null;

  const handleInterestingZ = useCallback(() => {
    if (topZ) onZAxisChange(topZ.field);
  }, [topZ, onZAxisChange]);

  // Build dropdown options: ranked candidates first (with score), then remaining
  const rankedFields = useMemo(() => new Set(rankedZ.map(r => r.field)), [rankedZ]);
  const unrankedCols = useMemo(
    () => columns.filter(c => !rankedFields.has(c.field)),
    [columns, rankedFields],
  );

  return (
    <>
      <div style={{ width: 1, height: 16, background: "var(--gray-a5)", margin: "0 2px" }} />
      <label style={{ fontSize: 11, color: zAxisKey ? "var(--blue-11)" : "var(--gray-11)", whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 4 }}>
        Z (3D):
        <select
          value={zAxisKey ?? ""}
          onChange={e => onZAxisChange(e.target.value || undefined)}
          style={{ ...SELECT, fontSize: 11, maxWidth: 160, borderColor: zAxisKey ? "var(--blue-8)" : undefined }}
        >
          <option value="">Off</option>
          {rankedZ.length > 0 && (
            <optgroup label="Interesting surfaces">
              {rankedZ.slice(0, 8).map(r => (
                <option key={r.field} value={r.field}>
                  {r.label ?? r.field} ({(r.score * 100).toFixed(0)}%)
                </option>
              ))}
            </optgroup>
          )}
          {unrankedCols.length > 0 && (
            <optgroup label={rankedZ.length > 0 ? "Other columns" : "Columns"}>
              {unrankedCols.map(col => (
                <option key={col.field} value={col.field}>{col.headerName ?? col.field}</option>
              ))}
            </optgroup>
          )}
        </select>
      </label>
      {topZ && topZ.score > 0.15 && (
        <button
          onClick={handleInterestingZ}
          title={`Best Z: ${topZ.label ?? topZ.field} (score ${(topZ.score * 100).toFixed(0)}%)`}
          style={{
            ...BTN,
            fontSize: 11,
            padding: "1px 6px",
            background: zAxisKey === topZ.field ? "var(--blue-9)" : "var(--gray-a3)",
            color: zAxisKey === topZ.field ? "white" : "var(--blue-11)",
            borderColor: "var(--blue-a6)",
          }}
        >
          Interesting Z
        </button>
      )}
    </>
  );
});
